import './RadioGroup.scss';

import * as React from 'react';

import flattenChildren from 'react-keyed-flatten-children';

import { Inline } from '../Inline';
import { Stack } from '../Stack';
import { RadioGroupContext, RadioItemContext } from './RadioGroupContext';
import { RadioItemProps } from './RadioItem';

type FormElementProps = React.AllHTMLAttributes<HTMLFormElement>;

export interface RadioGroupProps<Value = NonNullable<string | number>> {
  children: React.ReactElement<RadioItemProps>[];
  id: NonNullable<FormElementProps['id']>;
  disabled?: FormElementProps['disabled'];
  label?: FormElementProps['label'];
  required?: boolean;
  value: Value;
  onChange: (event: React.FormEvent<HTMLInputElement>) => void;
  name?: string;
  layout?: 'inline' | 'stack';
  'aria-describedby'?: string;
}

const layoutMap = new Map([
  ['inline', Inline] as const,
  ['stack', Stack] as const,
]);

/**
 * The RadioGroup provides an accessible way to group and control a
 * set of RadioItem components. The RadioGroup is responsible for
 * handling the value and disabled state—determining the presentation
 * and selection of the items in the list.
 *
 * It follows the [WAI-ARIA Radio Group pattern](https://www.w3.org/TR/wai-aria-practices/#radiobutton)
 * for radio groups not contained in a toolbar.
 *
 * RadioItem components don't render any visible UI themselves, but
 * they do provide `checked` and `disabled` as render props for
 * custom indicator child components. RadioItems are laid out with an
 * Inline or Stack component with 1rem of space between each. Options for
 * RadioGroup layout can be expanded as needs arise.
 *
 * @see https://www.w3.org/TR/wai-aria-practices/#radiobutton
 */
export function RadioGroup({
  children,
  id,
  disabled,
  label,
  value,
  onChange,
  name,
  layout = 'inline',
  ...props
}: RadioGroupProps): React.ReactElement {
  const radioItems = flattenChildren(children);
  const Layout = layoutMap.get(layout);

  return (
    <fieldset
      className="m-0 p-0 border-0"
      id={id}
      {...props}
      role="radiogroup"
      aria-label={label}
    >
      <RadioGroupContext.Provider
        value={{ id, disabled, value, name: name || id, onChange }}
      >
        <Layout space="4">
          {radioItems.map((item, i) => (
            <RadioItemContext.Provider key={i} value={i}>
              {item}
            </RadioItemContext.Provider>
          ))}
        </Layout>
      </RadioGroupContext.Provider>
    </fieldset>
  );
}
